// Drains the punch outbox to the server. DOM-free on purpose: this module is
// imported by both the page and the service worker, so it must only rely on
// fetch, IndexedDB (via Dexie), Web Locks, and BroadcastChannel.
import {
  ApiError,
  buildTimeclockFormData,
  getMe,
  submitFieldNoteSync,
  submitTimeclockSync,
  toSyncDelayMsInt,
} from "@/lib/api";
import { clientLog } from "@/lib/client-logger";
import { db, type OutboxFieldNote, type OutboxPunch } from "@/lib/db";
import {
  markAllAuthRequired,
  markAllFieldNotesAuthRequired,
  markAuthRequired,
  markFailed,
  markFieldNoteFailed,
  markFieldNoteRetryLater,
  markFieldNoteSynced,
  markFieldNoteSyncing,
  markFieldNotesAuthRequired,
  markRetryLater,
  markSynced,
  markSyncing,
  syncableFieldNotes,
  syncablePunches,
} from "@/lib/outbox";

const SYNC_LOCK_NAME = "punch-sync";
export const SYNC_CHANNEL_NAME = "punch-sync";

/** Turnstile tokens are valid for ~5 minutes; leave headroom for the request. */
const TURNSTILE_TOKEN_MAX_AGE_MS = 4 * 60 * 1000;

export type SyncMessage =
  | { type: "synced"; uuid: string; serverEntryId: number }
  | { type: "queue-changed" }
  | { type: "auth-required" }
  | { type: "sync-failed"; uuid: string; message: string };

export type SyncResult = {
  synced: number;
  halted: boolean;
  reason?: "offline" | "auth" | "failed" | "locked" | "backoff" | "empty" | "no-locks";
};

export function broadcast(message: SyncMessage): void {
  try {
    const channel = new BroadcastChannel(SYNC_CHANNEL_NAME);
    channel.postMessage(message);
    channel.close();
  } catch {
    // BroadcastChannel unavailable — UI falls back to Dexie live queries.
  }
}

export function backoffMs(attempts: number): number {
  return Math.min(5_000 * 2 ** attempts, 600_000);
}

type ErrorKind = "network" | "auth" | "transient" | "permanent";

export function classifyError(error: unknown): ErrorKind {
  if (error instanceof ApiError) {
    if (error.status === 401) return "auth";
    if (error.status === 408 || error.status === 425 || error.status === 429 || error.status >= 500) return "transient";
    return "permanent";
  }
  // fetch network failures surface as TypeError (or AbortError on timeouts).
  return "network";
}

function imageContentType(bytes: Uint8Array): string | null {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "image/jpeg";
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return "image/png";
  }
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return "image/webp";
  }
  return null;
}

async function uploadableSelfieBlob(punch: OutboxPunch): Promise<Blob | null> {
  const blob = punch.selfieBlob;
  if (!blob) return null;
  try {
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const contentType = imageContentType(bytes);
    if (!contentType) {
      clientLog("warn", "sync:punch:selfie_omitted_invalid_image", {
        id: punch.id,
        uuid: uuidTag(punch.uuid),
        blobType: blob.type,
        blobBytes: blob.size,
      });
      return null;
    }
    // Re-wrap the bytes so the multipart upload does not depend on an
    // IndexedDB-backed Blob handle that WebKit may fail to stream later.
    return new Blob([buffer], { type: contentType });
  } catch (error) {
    clientLog("warn", "sync:punch:selfie_omitted_unreadable", {
      id: punch.id,
      uuid: uuidTag(punch.uuid),
      blobType: blob.type,
      blobBytes: blob.size,
      errorName: (error as any)?.name,
      errorMessage: String((error as any)?.message || (error as any)?.name || ""),
    });
    return null;
  }
}

async function buildFormData(punch: OutboxPunch, includeToken: boolean): Promise<FormData> {
  const selfieBlob = await uploadableSelfieBlob(punch);
  return buildTimeclockFormData({
    fields: punch.fields,
    clientUuid: punch.uuid,
    selfieBlob,
    selfieName: punch.selfieName,
    syncDelayMs: toSyncDelayMsInt(Date.now() - punch.createdAt),
    turnstileToken: punch.turnstileToken,
    includeToken,
  });
}

function uuidTag(uuid: string): string {
  return uuid.slice(0, 8);
}

function isTurnstileRejection(error: unknown): boolean {
  return error instanceof ApiError && error.status === 400 && /turnstile/i.test(error.message);
}

/**
 * Sync one punch. Returns true to continue down the queue, false to halt.
 * Halting is the safety property: a clock_out is never submitted past a
 * clock_in that has not landed, because the server pairs a clock_out with the
 * most recent open clock_in.
 */
async function syncOne(punch: OutboxPunch, now: number): Promise<boolean> {
  const id = punch.id!;
  // Only send a Turnstile token while it is plausibly still valid; the server
  // accepts token-less submissions that carry a client_uuid.
  const tokenFresh =
    punch.turnstileToken !== null &&
    punch.attempts === 0 &&
    punch.tokenIssuedAt !== null &&
    now - punch.tokenIssuedAt < TURNSTILE_TOKEN_MAX_AGE_MS;

  await markSyncing(id);
  clientLog("info", "sync:punch:start", {
    id,
    uuid: uuidTag(punch.uuid),
    action: punch.action,
    attempts: punch.attempts,
    hasSelfie: !!punch.selfieBlob,
    includeToken: tokenFresh,
    online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
  });
  try {
    const { entry } = await submitTimeclockSync(await buildFormData(punch, tokenFresh));
    await markSynced(id, entry.id);
    clientLog("info", "sync:punch:success", {
      id,
      uuid: uuidTag(punch.uuid),
      serverEntryId: entry.id,
    });
    broadcast({ type: "synced", uuid: punch.uuid, serverEntryId: entry.id });
    return true;
  } catch (error) {
    const attempts = punch.attempts + 1;
    const status = error instanceof ApiError ? error.status : null;
    const message = error instanceof Error ? error.message : "Sync failed.";

    if (isTurnstileRejection(error) && tokenFresh) {
      // The captured token went stale between freshness check and server-side
      // validation. Not a real failure — retry this punch immediately without
      // the token so the server's authenticated client_uuid path can accept it.
      try {
        clientLog("warn", "sync:punch:retry_without_turnstile", {
          id,
          uuid: uuidTag(punch.uuid),
          message,
        });
        const { entry } = await submitTimeclockSync(await buildFormData({ ...punch, turnstileToken: null }, false));
        await markSynced(id, entry.id);
        clientLog("info", "sync:punch:success_after_turnstile_retry", {
          id,
          uuid: uuidTag(punch.uuid),
          serverEntryId: entry.id,
        });
        broadcast({ type: "synced", uuid: punch.uuid, serverEntryId: entry.id });
        return true;
      } catch (retryError) {
        const retryStatus = retryError instanceof ApiError ? retryError.status : null;
        const retryMessage = retryError instanceof Error ? retryError.message : "Sync failed.";
        clientLog("error", "sync:punch:failure_after_turnstile_retry", {
          id,
          uuid: uuidTag(punch.uuid),
          kind: classifyError(retryError),
          status: retryStatus,
          message: retryMessage,
          attempts,
        });
        switch (classifyError(retryError)) {
          case "auth":
            await markAuthRequired(punch.userId);
            broadcast({ type: "auth-required" });
            break;
          case "network":
          case "transient":
            await markRetryLater(id, attempts, Date.now() + backoffMs(attempts), retryStatus, retryMessage);
            break;
          case "permanent":
            await markFailed(id, retryStatus, retryMessage);
            broadcast({ type: "sync-failed", uuid: punch.uuid, message: retryMessage });
            break;
        }
        return false;
      }
    }

    clientLog(classifyError(error) === "permanent" ? "error" : "warn", "sync:punch:failure", {
      id,
      uuid: uuidTag(punch.uuid),
      kind: classifyError(error),
      status,
      message,
      attempts,
      online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
    });

    switch (classifyError(error)) {
      case "auth":
        await markAuthRequired(punch.userId);
        broadcast({ type: "auth-required" });
        return false;
      case "network":
      case "transient":
        await markRetryLater(id, attempts, Date.now() + backoffMs(attempts), status, message);
        return false;
      case "permanent":
        await markFailed(id, status, message);
        broadcast({ type: "sync-failed", uuid: punch.uuid, message });
        return false;
    }
  }
}

/**
 * Sync one field note. Returns true to continue, false to halt (same classifyError
 * and auth/network rules as punches; notes have no turnstile or pairing constraints).
 */
async function syncOneFieldNote(note: OutboxFieldNote, now: number): Promise<boolean> {
  const id = note.id!;
  await markFieldNoteSyncing(id);
  clientLog("info", "sync:field_note:start", {
    id,
    uuid: uuidTag(note.uuid),
    attempts: note.attempts,
    online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
  });
  try {
    const payload = {
      ...note.fields,
      client_uuid: note.uuid,
      // include a delay for audit (how long it sat in outbox); integer ms only
      sync_delay_ms: toSyncDelayMsInt(now - note.createdAt),
    };
    const { note: serverNote } = await submitFieldNoteSync(payload);
    await markFieldNoteSynced(id, serverNote.id);
    clientLog("info", "sync:field_note:success", {
      id,
      uuid: uuidTag(note.uuid),
      serverNoteId: serverNote.id,
    });
    broadcast({ type: "synced", uuid: note.uuid, serverEntryId: serverNote.id });
    return true;
  } catch (error) {
    const attempts = note.attempts + 1;
    const status = error instanceof ApiError ? error.status : null;
    const message = error instanceof Error ? error.message : "Sync failed.";
    clientLog(classifyError(error) === "permanent" ? "error" : "warn", "sync:field_note:failure", {
      id,
      uuid: uuidTag(note.uuid),
      kind: classifyError(error),
      status,
      message,
      attempts,
      online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
    });

    switch (classifyError(error)) {
      case "auth":
        await markFieldNotesAuthRequired(note.userId);
        broadcast({ type: "auth-required" });
        return false;
      case "network":
      case "transient":
        await markFieldNoteRetryLater(id, attempts, Date.now() + backoffMs(attempts), status, message);
        return false;
      case "permanent":
        await markFieldNoteFailed(id, status, message);
        broadcast({ type: "sync-failed", uuid: note.uuid, message });
        return false;
    }
  }
}

/**
 * Drain the current user's outbox FIFO. Safe to call from any context, any
 * number of times: a Web Lock guarantees a single runner across tabs and the
 * service worker, and server-side client_uuid dedup makes replays harmless.
 */
export async function runSync(options?: { ignoreBackoff?: boolean }): Promise<SyncResult> {
  const isForced = !!options?.ignoreBackoff;
  // Only trust the coarse navigator.onLine flag for non-forced runs. A "force"
  // (manual Sync now, or post-punch kick) will still validate via getMe (or
  // fall back to lastUser cache) before draining. Submits themselves carry the
  // httpOnly cookie for auth.
  if (!isForced && typeof navigator !== "undefined" && !navigator.onLine) {
    return { synced: 0, halted: true, reason: "offline" };
  }
  if (!navigator.locks) {
    // Very old browser: run unguarded — dedup still prevents duplicates.
    return drainQueue(options);
  }
  const result = await navigator.locks.request(
    SYNC_LOCK_NAME,
    { ifAvailable: true },
    async (lock) => (lock ? drainQueue(options) : null),
  );
  return result ?? { synced: 0, halted: true, reason: "locked" };
}

async function drainQueue(options?: { ignoreBackoff?: boolean }): Promise<SyncResult> {
  let me: Awaited<ReturnType<typeof getMe>> | null = null;

  // Prefer the last known good user from IndexedDB (populated by AuthProvider's /me query).
  // This avoids a network roundtrip to /auth/me on every sync kick / punch / visibility.
  try {
    const cached = await db.kv.get("lastUser");
    if (cached?.value && typeof (cached.value as any).id === "number") {
      me = cached.value as Awaited<ReturnType<typeof getMe>>;
    }
  } catch {}

  const needsFreshCheck = !!options?.ignoreBackoff; // forced after punch/login -> verify session is still valid

  if (!me || needsFreshCheck) {
    try {
      const fresh = await getMe();
      me = fresh;
      // keep lastUser fresh
      await db.kv.put({ key: "lastUser", value: fresh }).catch(() => {});
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        await markAllAuthRequired();
        await markAllFieldNotesAuthRequired();
        broadcast({ type: "auth-required" });
        return { synced: 0, halted: true, reason: "auth" };
      }
      if (me) {
        // We have a cached identity; use the cookie on the submit calls.
        // getMe was only for freshness/validation.
      } else if (options?.ignoreBackoff) {
        return { synced: 0, halted: true, reason: "offline" };
      } else {
        return { synced: 0, halted: true, reason: "offline" };
      }
    }
  }

  if (!me) {
    return { synced: 0, halted: true, reason: "offline" };
  }

  // Drain punches first (their FIFO + halt-on-failure is critical for in/out pairing).
  const punchQueue = await syncablePunches(me.id);
  let synced = 0;
  if (punchQueue.length > 0) {
    let localSynced = 0;
    const drainPunch = async (idx: number): Promise<boolean> => {
      if (idx >= punchQueue.length) return true;
      const punch = punchQueue[idx];
      const now = Date.now();
      if (!options?.ignoreBackoff && punch.nextAttemptAt > now) {
        synced += localSynced;
        return false; // backoff
      }
      const proceed = await (async () => syncOne(punch, now))();
      if (!proceed) {
        synced += localSynced;
        return false; // failed
      }
      localSynced += 1;
      return drainPunch(idx + 1);
    };
    const ok = await drainPunch(0);
    synced += localSynced;
    if (!ok) {
      const reason = (!options?.ignoreBackoff && punchQueue.some(p => p.nextAttemptAt > Date.now())) ? "backoff" : "failed";
      return { synced, halted: true, reason };
    }
  }

  // Then field notes (no pairing constraints; we can be a bit more lenient but reuse same logic).
  const noteQueue = await syncableFieldNotes(me.id);
  {
    let localSynced2 = 0;
    const drainNote = async (idx: number): Promise<boolean> => {
      if (idx >= noteQueue.length) return true;
      const note = noteQueue[idx];
      const now = Date.now();
      if (!options?.ignoreBackoff && note.nextAttemptAt > now) {
        synced += localSynced2;
        return false;
      }
      const proceed = await (async () => syncOneFieldNote(note, now))();
      if (!proceed) {
        synced += localSynced2;
        return false;
      }
      localSynced2 += 1;
      return drainNote(idx + 1);
    };
    const ok2 = await drainNote(0);
    synced += localSynced2;
    if (!ok2) {
      return { synced, halted: true, reason: "backoff" };
    }
  }

  if (punchQueue.length === 0 && noteQueue.length === 0) {
    return { synced, halted: false, reason: "empty" };
  }
  return { synced, halted: false };
}
